import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { DirectoryWizardModal } from "./masters/DirectoryWizardModal";

// Directory-driven company/division/address/POC selector for the LR form.
// You can ONLY pick what's in the directory — use "＋ Add to directory" (the wizard)
// to add anything new. All values (GST/address/POC) auto-fill from the directory and
// are shown read-only so you can see exactly what will print on the LR.
// `value`/`onSelect` use a normalised shape: { company, division, gst, address,
// poc_name, poc_phone, poc_email } (+ company_id/department_id/contact_id).
export function DirectoryPicker({ customers: customersProp = [], reloadCustomers, kind = "all", showAddress = true, value = {}, onSelect, testPrefix = "dir" }) {
  const [companies, setCompanies] = useState(customersProp || []);
  const [companyId, setCompanyId] = useState("");
  const [bundle, setBundle] = useState(null);
  const [deptId, setDeptId] = useState("");
  const [addrId, setAddrId] = useState("");
  const [pocId, setPocId] = useState("");
  const [wizOpen, setWizOpen] = useState(false);

  async function loadCompanies() {
    const { data } = await api.get("/customers");
    setCompanies(data || []);
  }
  useEffect(() => { loadCompanies(); }, []);

  // Instant sync: reload when any directory write happens anywhere in the app.
  useEffect(() => {
    const h = async () => {
      await loadCompanies();
      if (companyId) {
        const r = await api.get(`/directory/company/${companyId}`).catch(() => null);
        if (r) setBundle(r.data);
      }
    };
    window.addEventListener("directory-changed", h);
    return () => window.removeEventListener("directory-changed", h);
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Edit mode: preselect the company if its name is already on the record.
  useEffect(() => {
    if (value.company && !companyId) {
      const c = companies.find((x) => x.name === value.company);
      if (c) setCompanyId(c.id);
    }
  }, [value.company, companies]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) { setBundle(null); return; }
    api.get(`/directory/company/${companyId}`).then((r) => setBundle(r.data)).catch(() => setBundle(null));
  }, [companyId]);

  const depts = bundle?.departments || [];
  const dept = depts.find((d) => d.id === deptId);
  const addrs = (bundle?.addresses || []).filter(
    (a) => (!deptId || a.department_id === deptId) && (kind === "all" || a.kind === kind || a.kind === "both"));
  const pocs = (bundle?.employees || []).filter((e) => !deptId || e.department_id === deptId);

  const emit = (patch) => onSelect?.(patch);

  function pickCompany(id) {
    setCompanyId(id); setDeptId(""); setAddrId(""); setPocId("");
    const c = companies.find((x) => x.id === id);
    emit({ company_id: id, company: c?.name || "", department_id: "", contact_id: "",
           division: "", gst: "", address: "", poc_name: "", poc_phone: "", poc_email: "" });
  }
  function pickDept(id) {
    setDeptId(id); setAddrId(""); setPocId("");
    const d = depts.find((x) => x.id === id);
    emit({ department_id: id, division: d?.name || "", gst: d?.gst_number || "" });
  }
  function pickAddress(id) {
    setAddrId(id);
    const a = (bundle?.addresses || []).find((x) => x.id === id);
    if (a) emit({ address: a.address, gst: a.gst || dept?.gst_number || "" });
  }
  function pickPoc(id) {
    setPocId(id);
    const p = (bundle?.employees || []).find((x) => x.id === id);
    if (p) emit({ contact_id: id, poc_name: p.name, poc_phone: p.phone, poc_email: p.email });
  }
  async function afterWizard() {
    await loadCompanies();
    if (reloadCustomers) await reloadCustomers();
    if (companyId) {
      const r = await api.get(`/directory/company/${companyId}`).catch(() => null);
      if (r) setBundle(r.data);
    }
  }

  const hasSummary = value.gst || value.address || value.poc_name || value.poc_phone || value.poc_email;

  return (
    <div className="border border-[#E5E3DB] rounded p-3 bg-[#FAFAF7]" data-testid={`${testPrefix}-picker`}>
      <div className={`grid grid-cols-1 ${showAddress ? "md:grid-cols-4" : "md:grid-cols-3"} gap-2`}>
        <div>
          <label className="input-label">Company</label>
          <select className="input-field" value={companyId} onChange={(e) => pickCompany(e.target.value)} data-testid={`${testPrefix}-company`}>
            <option value="">Select company…</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="input-label">Division</label>
          <select className="input-field" value={deptId} onChange={(e) => pickDept(e.target.value)} disabled={!companyId} data-testid={`${testPrefix}-division`}>
            <option value="">{depts.length ? "Select division…" : "No divisions"}</option>
            {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        {showAddress && (
          <div>
            <label className="input-label">Address</label>
            <select className="input-field" value={addrId} onChange={(e) => pickAddress(e.target.value)} disabled={!companyId} data-testid={`${testPrefix}-address`}>
              <option value="">{addrs.length ? "Select address…" : "No saved addresses"}</option>
              {addrs.map((a) => <option key={a.id} value={a.id}>{a.label || a.address}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="input-label">POC</label>
          <select className="input-field" value={pocId} onChange={(e) => pickPoc(e.target.value)} disabled={!companyId} data-testid={`${testPrefix}-poc`}>
            <option value="">{pocs.length ? "Select POC…" : "No POCs"}</option>
            {pocs.map((p) => <option key={p.id} value={p.id}>{p.name}{p.designation ? ` · ${p.designation}` : ""}</option>)}
          </select>
        </div>
      </div>

      {hasSummary && (
        <div className="mt-2 text-xs text-[#68665E] space-y-0.5" data-testid={`${testPrefix}-summary`}>
          {value.gst && <div><span className="font-semibold text-[#4A4842]">GST:</span> <span className="font-mono">{value.gst}</span></div>}
          {value.address && <div><span className="font-semibold text-[#4A4842]">Address:</span> {value.address}</div>}
          {(value.poc_name || value.poc_phone || value.poc_email) && (
            <div><span className="font-semibold text-[#4A4842]">POC:</span> {[value.poc_name, value.poc_phone, value.poc_email].filter(Boolean).join(" · ")}</div>
          )}
        </div>
      )}

      <button type="button" className="text-[11px] text-[#C84B31] hover:underline mt-2 inline-flex items-center gap-1" onClick={() => setWizOpen(true)} data-testid={`${testPrefix}-add`}>
        <Plus className="w-3 h-3" /> Add to directory
      </button>
      <DirectoryWizardModal open={wizOpen} onClose={() => setWizOpen(false)} onChange={afterWizard} />
    </div>
  );
}
